import { useState, useCallback } from 'react'

import { useStartNotetakerTrialMutation } from 'common'
import { USER_HAS_MEETING_ROUTE, MI_MEETINGS_TOUR } from 'features/meeting_intelligence'

export const NOTETAKER_SCOPE_EXTERNAL = 'external'
export const NOTETAKER_SCOPE_ALL = 'all'
export const RECAP_EMAIL_RECIPIENTS_ALL = 'all'
export const RECAP_EMAIL_RECIPIENTS_ONLY_ME = 'only_me'

export const NOTETAKER_TRIAL_DEFAULT_SETTINGS = {
  NOTETAKER_SCOPE: NOTETAKER_SCOPE_EXTERNAL,
  RECAP_EMAIL_RECIPIENTS: RECAP_EMAIL_RECIPIENTS_ALL,
}

const DEFAULT_REDIRECT_PATH = `${USER_HAS_MEETING_ROUTE}?${MI_MEETINGS_TOUR.STEP_ONE}=true`

/**
 * Hook to start a notetaker trial for free users.
 *
 * This hook encapsulates the logic for starting a notetaker trial,
 * handling loading states, and redirecting after success.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.redirectPath - Path to redirect to after trial starts (default: meetings page with tour)
 * @param {boolean} options.shouldRedirect - Whether to redirect after trial starts (default: true)
 * @param {Function} options.onSuccess - Callback to run after successful trial start
 * @param {Function} options.onError - Callback to run if trial start fails
 * @returns {Object} Object containing startTrial function and state
 */
export const useStartNotetakerTrial = ({
  redirectPath = DEFAULT_REDIRECT_PATH,
  shouldRedirect = true,
  onSuccess,
  onError,
} = {}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [startNotetakerTrialMutation] = useStartNotetakerTrialMutation()

  /**
   * Start the notetaker trial with the given settings.
   *
   * @param {Object} settings - Trial settings
   * @param {string} settings.notetakerScope - 'external' (meetings with external invitees) or 'all' (all meetings)
   * @param {string} settings.recapEmailRecipients - 'all' (all attendees) or 'only_me' (only the user)
   */
  const startTrial = useCallback(async ({
    notetakerScope = NOTETAKER_TRIAL_DEFAULT_SETTINGS.NOTETAKER_SCOPE,
    recapEmailRecipients = NOTETAKER_TRIAL_DEFAULT_SETTINGS.RECAP_EMAIL_RECIPIENTS,
  } = {}) => {
    setIsLoading(true)
    setError(null)

    try {
      await startNotetakerTrialMutation({ notetakerScope, recapEmailRecipients }).unwrap()

      setIsSuccess(true)
      setIsLoading(false)
      onSuccess?.()

      if (shouldRedirect) {
        window.location.href = redirectPath
      }
    } catch (err) {
      console.error('Error starting notetaker trial:', err)
      setError(err)
      setIsLoading(false)
      onError?.(err)
    }
  }, [startNotetakerTrialMutation, redirectPath, shouldRedirect, onSuccess, onError])

  return {
    startTrial,
    isLoading,
    isSuccess,
    error,
  }
}
